import React from 'react';
import { Link } from 'react-router-dom';
import { PageTitle } from '../components/PageTitle';
import { ArrowLeftIcon, BarChart2Icon, TrendingUpIcon, UsersIcon, MailIcon } from 'lucide-react';
export function CaseStudy3() {
  return <div data-id="element-589">
      <PageTitle title="CRM Program Development" description="How I built Neutrogena's CRM program from the ground up, fueling +236% traffic and +53% growth ($540M impact)." data-id="element-590" />
      <section className="bg-gradient-to-r from-[#0096B4] to-[#7DCCD8] text-white py-16" data-id="element-591">
        <div className="container mx-auto px-4" data-id="element-592">
          <Link to="/" className="inline-flex items-center text-white hover:text-[#E0F5F8] mb-6 transition-colors duration-200" data-id="element-593">
            <ArrowLeftIcon size={16} className="mr-1" data-id="element-594" />
            Back to Home
          </Link>
          <h1 className="text-4xl font-bold mb-4" data-id="element-595">CRM Program Development</h1>
          <p className="text-xl mb-6 text-white max-w-3xl" data-id="element-596">
            How I built Neutrogena's CRM program from the ground up, fueling
            +236% traffic and +53% growth ($540M impact).
          </p>
          <div className="flex flex-wrap gap-6 mt-8" data-id="element-597">
            <div className="flex items-center" data-id="element-598">
              <BarChart2Icon size={20} className="mr-2 text-[#E0F5F8]" data-id="element-599" />
              <span className="text-white" data-id="element-600">+236% Traffic Growth</span>
            </div>
            <div className="flex items-center" data-id="element-601">
              <TrendingUpIcon size={20} className="mr-2 text-[#E0F5F8]" data-id="element-602" />
              <span className="text-white" data-id="element-603">+53% Sales Growth</span>
            </div>
            <div className="flex items-center" data-id="element-604">
              <UsersIcon size={20} className="mr-2 text-[#E0F5F8]" data-id="element-605" />
              <span className="text-white" data-id="element-606">300k+ Subscribers</span>
            </div>
          </div>
        </div>
      </section>
      <section className="py-16 bg-white" data-id="element-607">
        <div className="container mx-auto px-4" data-id="element-608">
          <div className="max-w-3xl mx-auto" data-id="element-609">
            <div className="mb-12" data-id="element-610">
              <h2 className="text-2xl font-bold text-gray-900 mb-4" data-id="element-611">
                The Challenge
              </h2>
              <p className="text-gray-700 mb-4" data-id="element-612">
                When I joined Neutrogena, the brand had a minimal digital
                presence and no structured customer relationship management
                program despite being a market leader in skincare.
              </p>
              <ul className="list-disc pl-6 mb-4 text-gray-700 space-y-2" data-id="element-613">
                <li data-id="element-614">
                  The brand was missing significant opportunities to engage
                  directly with consumers
                </li>
                <li data-id="element-615">
                  There was no system for gathering first-party data to drive
                  personalization
                </li>
                <li data-id="element-616">
                  The company lacked mechanisms to drive loyalty and repeat
                  purchases
                </li>
              </ul>
            </div>
            <div className="mb-12" data-id="element-617">
              <h2 className="text-2xl font-bold text-gray-900 mb-4" data-id="element-618">
                My Approach
              </h2>
              <ol className="list-decimal pl-6 space-y-6" data-id="element-619">
                <li data-id="element-620">
                  <div data-id="element-621">
                    <span className="font-medium text-gray-900" data-id="element-622">
                      Consumer Research & Segmentation
                    </span>
                    <ul className="list-disc pl-6 mt-2 text-gray-700 space-y-1" data-id="element-623">
                      <li data-id="element-624">
                        Developed six distinct consumer segments across 300,000+
                        subscribers, each with unique needs and preferences
                      </li>
                      <li data-id="element-625">
                        Created granular segmentation that allowed for highly
                        targeted messaging and offers
                      </li>
                    </ul>
                  </div>
                </li>
                <li data-id="element-626">
                  <div data-id="element-627">
                    <span className="font-medium text-gray-900" data-id="element-628">
                      Data Infrastructure Development
                    </span>
                    <ul className="list-disc pl-6 mt-2 text-gray-700 space-y-1" data-id="element-629">
                      <li data-id="element-630">
                        Designed and implemented new data architecture
                        integrating multiple sources to create unified customer
                        profiles
                      </li>
                      <li data-id="element-631">
                        Built sophisticated segmentation and targeting
                        capabilities that enabled personalized communications
                      </li>
                    </ul>
                  </div>
                </li>
                <li data-id="element-632">
                  <div data-id="element-633">
                    <span className="font-medium text-gray-900" data-id="element-634">
                      Content & Engagement Strategy
                    </span>
                    <ul className="list-disc pl-6 mt-2 text-gray-700 space-y-1" data-id="element-635">
                      <li data-id="element-636">
                        Developed comprehensive educational content strategy
                        aligned with the customer journey
                      </li>
                      <li data-id="element-637">
                        Implemented systematic A/B testing to optimize messaging
                        effectiveness across touchpoints
                      </li>
                    </ul>
                  </div>
                </li>
              </ol>
            </div>
            <div className="mb-12" data-id="element-638">
              <h2 className="text-2xl font-bold text-gray-900 mb-4" data-id="element-639">
                Execution & Results
              </h2>
              <div className="bg-[#F0FAFC] p-6 rounded-lg mb-8" data-id="element-640">
                <h3 className="text-xl font-semibold text-gray-800 mb-3" data-id="element-641">
                  Key Initiatives Implemented:
                </h3>
                <ul className="space-y-3" data-id="element-642">
                  <li className="flex" data-id="element-643">
                    <div className="w-2 h-2 rounded-full bg-[#0096B4] mt-2 mr-3" data-id="element-644"></div>
                    <div data-id="element-645">
                      <span className="font-medium text-gray-900" data-id="element-646">
                        Personalized Email Program
                      </span>
                      <p className="text-gray-700" data-id="element-647">
                        Created segment-specific email journeys with dynamic
                        content based on user behavior and preferences
                      </p>
                    </div>
                  </li>
                  <li className="flex" data-id="element-648">
                    <div className="w-2 h-2 rounded-full bg-[#0096B4] mt-2 mr-3" data-id="element-649"></div>
                    <div data-id="element-650">
                      <span className="font-medium text-gray-900" data-id="element-651">
                        Skin Diagnostic Tool
                      </span>
                      <p className="text-gray-700" data-id="element-652">
                        Developed interactive assessment tool that provided
                        personalized skincare regimen recommendations
                      </p>
                    </div>
                  </li>
                  <li className="flex" data-id="element-653">
                    <div className="w-2 h-2 rounded-full bg-[#0096B4] mt-2 mr-3" data-id="element-654"></div>
                    <div data-id="element-655">
                      <span className="font-medium text-gray-900" data-id="element-656">
                        Cross-Channel Integration
                      </span>
                      <p className="text-gray-700" data-id="element-657">
                        Connected online engagement with in-store experiences
                        through QR codes and personalized offers
                      </p>
                    </div>
                  </li>
                </ul>
              </div>
              <h3 className="text-xl font-semibold text-gray-800 mb-3" data-id="element-658">
                Impact:
              </h3>
              <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mb-6" data-id="element-659">
                <div className="bg-[#E0F5F8] p-4 rounded-lg text-center" data-id="element-660">
                  <div className="text-3xl font-bold text-[#0096B4] mb-1" data-id="element-661">
                    +236%
                  </div>
                  <div className="text-sm text-gray-700" data-id="element-662">
                    Website Traffic Growth
                  </div>
                </div>
                <div className="bg-[#E0F5F8] p-4 rounded-lg text-center" data-id="element-663">
                  <div className="text-3xl font-bold text-[#0096B4] mb-1" data-id="element-664">
                    +53%
                  </div>
                  <div className="text-sm text-gray-700" data-id="element-665">Sales Growth</div>
                </div>
                <div className="bg-[#E0F5F8] p-4 rounded-lg text-center" data-id="element-666">
                  <div className="text-3xl font-bold text-[#0096B4] mb-1" data-id="element-667">
                    $540M
                  </div>
                  <div className="text-sm text-gray-700" data-id="element-668">Commercial Impact</div>
                </div>
              </div>
              <p className="text-gray-700" data-id="element-669">
                The CRM program delivered exceptional results across all key
                metrics. Website traffic increased by 236% year-over-year, while
                the brand experienced 53% sales growth on its direct-to-consumer
                channel, a $540+ million impact. The optimizations also
                increased email open rates by 28% and boosted customer retention
                rates by 42%.
              </p>
            </div>
            <div data-id="element-670">
              <h2 className="text-2xl font-bold text-gray-900 mb-4" data-id="element-671">
                Key Learnings
              </h2>
              <ol className="list-decimal pl-6 space-y-4" data-id="element-672">
                <li data-id="element-673">
                  <div data-id="element-674">
                    <span className="font-medium text-gray-900" data-id="element-675">
                      Personalization drives engagement and conversion
                    </span>
                    <p className="text-gray-700" data-id="element-676">
                      By creating segment-specific content and recommendations,
                      we significantly increased engagement metrics and
                      conversion rates, proving the value of targeted
                      communications.
                    </p>
                  </div>
                </li>
                <li data-id="element-677">
                  <div data-id="element-678">
                    <span className="font-medium text-gray-900" data-id="element-679">
                      First-party data creates strategic advantage
                    </span>
                    <p className="text-gray-700" data-id="element-680">
                      The program generated valuable insights that informed
                      product development, marketing strategy, and retail
                      partnerships, creating value beyond direct marketing.
                    </p>
                  </div>
                </li>
                <li data-id="element-681">
                  <div data-id="element-682">
                    <span className="font-medium text-gray-900" data-id="element-683">
                      Systematic testing creates continuous improvement
                    </span>
                    <p className="text-gray-700" data-id="element-684">
                      The data-driven approach to A/B testing created a culture
                      that continuously improved program performance,
                      establishing a sustainable model for long-term growth.
                    </p>
                  </div>
                </li>
              </ol>
            </div>
          </div>
        </div>
      </section>
      <section className="py-12 bg-[#F0FAFC]" data-id="element-685">
        <div className="container mx-auto px-4" data-id="element-686">
          <div className="max-w-3xl mx-auto" data-id="element-687">
            <h2 className="text-2xl font-bold text-gray-900 mb-6" data-id="element-688">
              More Case Studies
            </h2>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-6" data-id="element-689">
              <Link to="/case-study-1" className="bg-white p-6 rounded-lg shadow-md hover:shadow-lg transition-shadow duration-200" data-id="element-690">
                <h3 className="text-xl font-semibold text-gray-800 mb-2" data-id="element-691">
                  Product Turnaround Strategy
                </h3>
                <p className="text-gray-600 mb-3" data-id="element-692">
                  Drove turnaround of $110M product platform from -12% decline
                  to +9% growth.
                </p>
                <span className="text-[#0096B4] font-medium" data-id="element-693">
                  Read case study →
                </span>
              </Link>
              <Link to="/case-study-2" className="bg-white p-6 rounded-lg shadow-md hover:shadow-lg transition-shadow duration-200" data-id="element-694">
                <h3 className="text-xl font-semibold text-gray-800 mb-2" data-id="element-695">
                  Go-to-Market Strategy
                </h3>
                <p className="text-gray-600 mb-3" data-id="element-696">
                  Spearheaded new GTM strategy, driving 93% trial-to-purchase
                  conversion.
                </p>
                <span className="text-[#0096B4] font-medium" data-id="element-697">
                  Read case study →
                </span>
              </Link>
            </div>
          </div>
        </div>
      </section>
    </div>;
}